import { useEffect, useState, useCallback } from "react";

import { Dropdown } from "../../assets/icons";
import { SearchIcon } from "../../assets/icons";
import { Calender } from "../../assets/icons";
import { orders } from "../../data/DemoData";
import {
  AccountContainer,
  ExternalContainer,
} from "../../components/Account/reusables";
import {
  getMyTransactions,
  updateFilters,
  clearFilters,
} from "../../features/store/transactionSlice";
import { RootState } from "../../redux/store";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import {
  FilterState,
  OrderSearchFilterProps,
  OrderTableProps,
  PaginationProps,
} from "./types";
import { TransactionDetailsModal } from "./TransactionDetailModal";

export const OrderHistory = () => {
  const dispatch = useAppDispatch();
  const { transactions, pagination, loading, error, user } = useAppSelector(
    (state: RootState) => state.transactions,
  );
  const { data: appData } = useAppSelector((state: RootState) => state.appData);
  const currencies = appData?.currencies || [];

  const [filters, setFilters] = useState<FilterState>({
    search: "",
    start_date: "",
    end_date: "",
    status: "",
    type: "",
    page: 1,
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch transactions when filters change
  const fetchTransactions = useCallback(() => {
    const params: any = {
      page: filters.page,
      per_page: 15,
    };

    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.status) params.status = filters.status;
    if (filters.type) params.type = filters.type;

    dispatch(getMyTransactions(params));
  }, [
    dispatch,
    filters.page,
    debouncedSearch,
    filters.start_date,
    filters.end_date,
    filters.status,
    filters.type,
  ]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleFilterChange = (
    key: keyof FilterState,
    value: string | number,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: key === "page" ? Number(value) : value,
      page: key !== "page" ? 1 : Number(value),
    }));
  };
  const handleClearFilters = () => {
    setFilters({
      search: "",
      start_date: "",
      end_date: "",
      status: "",
      type: "",
      page: 1,
    });
    dispatch(clearFilters());
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  return (
    <ExternalContainer>
      <div className="h-screen">
        <AccountContainer headerText="Order History">
          <div className="flex flex-col">
            <OrderSearchFilter
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              loading={loading}
            />
            <div className="border border-[#FFFFFF1A]">
              <OrderTable
                transactions={transactions}
                loading={loading}
                formatDate={formatDate}
                formatTime={formatTime}
                currencies={currencies}
              />
              {pagination && (
                <Pagination
                  pagination={pagination}
                  currentPage={filters.page}
                  onPageChange={(page) => handleFilterChange("page", page)}
                  loading={loading}
                />
              )}
            </div>
          </div>
        </AccountContainer>
      </div>
    </ExternalContainer>
  );
};

const OrderSearchFilter: React.FC<OrderSearchFilterProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  loading,
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "0", label: "Successful" },
    { value: "1", label: "Pending" },
    { value: "2", label: "Paid" },
    { value: "3", label: "Queued" },
    { value: "6", label: "Failed" },
    { value: "5", label: "Reversed" },
  ];

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  const handleDateRangeChange = (type: "start" | "end", value: string) => {
    if (type === "start") {
      onFilterChange("start_date", value);
    } else {
      onFilterChange("end_date", value);
    }
  };

  return (
    <div className="flex flex-col bp:flex-row bp:items-center bp:justify-between mb-4 gap-2">
      <div className="relative">
        <input
          type="text"
          placeholder="Search for an order..."
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          disabled={loading}
          className="border p-2 pl-10 rounded-3xl w-[260px] h-[40px] bp:h-[48px] bp:w-[500px] dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A]"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <SearchIcon />
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative">
          {/* <input
            type="text"
            placeholder="DD-MM-YY - DD-MM-YY"
            value={filters.start_date}
            onChange={(e) => handleDateRangeChange("start", e.target.value)}
            disabled={loading}
            className="border p-2 pl-10 bg-[#F4F4F5] w-[260px] h-[40px] md:w-[273px] rounded-3xl dark:bg-[#FFFFFF1A] dark:text-white dark:border-[#FFFFFF1A]"
          /> */}
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => handleDateRangeChange("start", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[125px] h-[40px] rounded-3xl dark:bg-[#FFFFFF1A] dark:text-white dark:border-[#FFFFFF1A] disabled:opacity-50"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => handleDateRangeChange("end", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[125px] h-[40px] rounded-3xl dark:bg-[#FFFFFF1A] dark:text-white dark:border-[#FFFFFF1A] disabled:opacity-50"
            placeholder="End Date"
          />
          {/* <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Calender />
          </div> */}
        </div>
        <div className="relative">
          <button
            className="border p-2 bg-[#F4F4F5] w-[260px] h-[40px] md:w-[100px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            disabled={loading}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm">
                {" "}
                {statusOptions.find((opt) => opt.value === filters.status)
                  ?.label || "Status"}
              </p>{" "}
              <Dropdown />
            </div>
          </button>
          {showStatusDropdown && (
            <div className="absolute top-full mt-1 w-full bg-white dark:bg-[#1a1a1a] border border-[#FFFFFF1A] rounded-md shadow-lg z-10">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] text-sm"
                  onClick={() => {
                    onFilterChange("status", option.value);
                    setShowStatusDropdown(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={onClearFilters}
          disabled={loading}
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-3xl text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-100 dark:hover:bg-[#FFFFFF33] disabled:opacity-50"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

const OrderTable: React.FC<OrderTableProps> = ({
  transactions,
  loading,
  formatDate,
  formatTime,
  currencies,
}) => {
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAppSelector((state) => state.auth);

  const handleRowClick = (purchase: any) => {
    const normalizedTransaction = { ...purchase };

    const price = formatAmountWithSymbol(
      purchase.price,
      getCurrencySymbol(purchase.currency_id) || purchase.currency_symbol || "",
    );

    const amount = JSON.parse(purchase.metadata)?.amount;
    let formattedNumber = amount || "";
    if (amount) {
      formattedNumber = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    }
    const display =
      purchase.type_text === "AIRTIME" ? `${formattedNumber} Credits` : amount;

    if (!purchase.items_details || purchase.items_details.length === 0) {
      normalizedTransaction.items_details = [
        {
          name:
            purchase.description || purchase.type_text || "Transaction Item",
          username: user?.username ? user.username : user.firstname,
          image: "",
          currency_amount: display || "",
          price,
        },
      ];
    }

    normalizedTransaction.subtotal = purchase.subtotal || purchase.price;
    normalizedTransaction.fee = purchase.fee || "0.00";

    setSelectedTransaction(normalizedTransaction);
    setIsModalOpen(true);
  };

  const getCurrencySymbol = (currencyId?: number) => {
    const match = currencies.find(
      (currency: any) => currency.id === currencyId,
    );
    return match?.symbol || "";
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const formatAmountWithSymbol = (
    amount: string | number | undefined,
    symbol: string,
  ) => {
    if (amount === undefined || amount === null || amount === "") return "-";

    const numericAmount =
      typeof amount === "string" ? parseFloat(amount) : amount;

    if (isNaN(numericAmount)) return "-";

    const formattedNumber = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numericAmount);

    if (!symbol) return formattedNumber;

    const normalized = symbol.toLowerCase();
    const spacer = /^[A-Za-z]+$/.test(symbol) ? " " : "";
    if (
      normalized === "coins" ||
      normalized === "point" ||
      normalized === "points"
    ) {
      return `${formattedNumber}${spacer}${symbol}`;
    }

    return `${symbol}${spacer}${formattedNumber}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
            No transactions found
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            Try adjusting your search criteria or date range
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-6 text-left sm:hidden">STATUS</th>
              <th className="px-6 text-left">Type</th>
              <th className="px-6 text-left hidden sm:table-cell">Date</th>
              <th className="px-6 text-left hidden sm:table-cell">Time</th>
              <th className="px-6 text-left hidden sm:table-cell">Status</th>

              <th className="px-6 text-left">Amount</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm bg-white dark:bg-[#1a1a1a]">
            {transactions.map((transaction) => (
              <tr
                key={transaction.id}
                className="border-b border-gray-200 h-[50px] hover:bg-gray-50 dark:hover:bg-gray-50/5 cursor-pointer"
                onClick={() => handleRowClick(transaction)}
              >
                <td className="sm:hidden px-6">
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                        transaction.status_text === "PENDING"
                          ? "bg-orange-500"
                          : transaction.status_text === "SUCCESSFUL" ||
                              transaction.status_text === "PAID"
                            ? "bg-green-500"
                            : transaction.status_text === "FAILED"
                              ? "bg-red-500"
                              : "bg-blue-500"
                      }`}
                    />
                    <span>{transaction.id}</span>
                  </div>
                </td>

                <td className="px-6">{transaction.type_text}</td>
                <td className="px-6 hidden sm:table-cell">
                  {" "}
                  {formatDate(transaction.created_at)}
                </td>
                <td className="px-6 hidden sm:table-cell">
                  {formatTime(transaction.created_at)}
                </td>
                <td className="px-6 hidden sm:table-cell">
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${
                      transaction.status_text === "PENDING"
                        ? "text-orange-500"
                        : transaction.status_text === "SUCCESSFUL"
                          ? "text-green-500"
                          : transaction.status_text === "PAID"
                            ? "text-green-500"
                            : transaction.status_text === "REVERSED"
                              ? "text-blue-500"
                              : transaction.status_text === "FAILED"
                                ? "text-red-500"
                                : transaction.status_text === "QUEUED"
                                  ? "text-yellow-500"
                                  : "text-gray-500"
                    }`}
                  >
                    {transaction.status_text}
                  </span>
                </td>

                <td className="px-6 font-bold">
                  {formatAmountWithSymbol(
                    transaction.price ?? transaction.amount,
                    getCurrencySymbol(transaction.currency_id) ||
                      transaction.currency_symbol ||
                      transaction.currency?.symbol ||
                      "",
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TransactionDetailsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        transaction={selectedTransaction}
        getCurrencySymbol={getCurrencySymbol}
        formatAmountWithSymbol={formatAmountWithSymbol}
        formatDate={formatDate}
        formatTime={formatTime}
      />
    </div>
  );
};

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  currentPage,
  onPageChange,
  loading,
}) => {
  const { current_page, last_page, from, to, total } = pagination;

  const handlePrevious = () => {
    if (current_page > 1) {
      onPageChange(current_page - 1);
    }
  };

  const handleNext = () => {
    if (current_page < last_page) {
      onPageChange(current_page + 1);
    }
  };

  return (
    <div className="flex justify-between items-center border-t border-[#FFFFFF1A] p-4">
      <button
        className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
        onClick={handlePrevious}
        disabled={current_page <= 1 || loading}
      >
        Previous
      </button>

      <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
        Showing {from || 0} to {to || 0} of {total || 0} results
        {last_page > 1 && ` (Page ${current_page} of ${last_page})`}
      </span>

      <button
        className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
        onClick={handleNext}
        disabled={current_page >= last_page || loading}
      >
        Next
      </button>
    </div>
  );
};
